import { useContext, type HTMLAttributes, type MouseEvent, type Ref } from "react";
import { StickyNote } from "lucide-react";
import { PRIORITY_LABEL, STATUS_LABEL, type TaskStatus } from "@desk/contracts";
import type { TaskRowProps } from "../types";
import { PriorityIcon, ProjectGlyph, StatusIcon } from "@/components/icons";
import { CarriedLabel, DeadlineLabel, RepeatBadge, RowHoverActions, SnoozeBadge, SyncBadge, TodayStar } from "@/components/task-bits";
import { Tooltip } from "@/components/ui/misc";
import { cn } from "@/lib/utils";
import { GroupLabelContext } from "./context";

/** Hairline pill used for row metadata (labels, deadline, location). */
export const metaChip =
  "inline-flex h-[22px] max-w-[180px] shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border border-border px-2 text-[12px] leading-none text-muted-foreground [&_svg]:size-3 [&_svg]:shrink-0";

/** Stable neutral tone for a label dot (the brand has no hue palette, so tones vary instead). */
export function labelTone(label: string): string {
  let h = 0;
  for (let i = 0; i < label.length; i++) h = (h * 31 + label.charCodeAt(i)) >>> 0;
  return `var(--console-dot-${(h % 4) + 1})`;
}

export function LabelPill({ label }: { label: string }) {
  return (
    <span className={metaChip}>
      <span aria-hidden className="size-[7px] shrink-0 rounded-full" style={{ background: labelTone(label) }} />
      <span className="truncate">{label}</span>
    </span>
  );
}

/** Linear status circle that doubles as the completion control. */
export function StatusToggle({
  status,
  onToggle,
  title,
  className,
}: {
  status: TaskStatus;
  onToggle: () => void;
  title: string;
  className?: string;
}) {
  const closed = status === "done" || status === "cancelled";
  return (
    <Tooltip content={closed ? "Reopen" : "Mark done"} shortcut="X">
      <button
        type="button"
        role="checkbox"
        aria-checked={closed}
        aria-label={`${closed ? "Reopen" : "Complete"} ${title} (${STATUS_LABEL[status]})`}
        onClick={(e) => {
          e.stopPropagation();
          onToggle();
        }}
        onKeyDown={(e) => e.stopPropagation()}
        className={cn(
          "group/st relative inline-flex size-5 shrink-0 items-center justify-center rounded-full outline-none transition-transform duration-100 active:scale-90 focus-visible:ring-2 focus-visible:ring-ring/60",
          className,
        )}
      >
        <StatusIcon status={status} className="size-[14px]" />
      </button>
    </Tooltip>
  );
}

type RowPassThrough = HTMLAttributes<HTMLDivElement> & { ref?: Ref<HTMLDivElement> };

export function ConsoleTaskRow(props: TaskRowProps) {
  const {
    task: t,
    selected,
    expanded,
    sync,
    location,
    today,
    timezone,
    plannedToday,
    carriedFrom,
    onToggleComplete,
    onOpen,
    onSelect,
    menu,
    trailing,
    quickActions,
    rowProps,
    chipEditors,
    locationIcon: _locationIcon,
    // Anything else comes from wrappers such as the context-menu trigger (onContextMenu, ref…).
    ...rest
  } = props as TaskRowProps & RowPassThrough;
  const groupLabel = useContext(GroupLabelContext);
  const closed = t.status === "done" || t.status === "cancelled";
  const doneItems = t.checklist.filter((c) => c.done).length;
  const place = location ?? groupLabel;
  const extraLabels = t.labels.length - 2;

  return (
    <div
      {...rest}
      {...rowProps}
      data-console-row
      onClick={(e: MouseEvent<HTMLDivElement>) => {
        rowProps.onClick?.(e as never);
        rest.onClick?.(e);
        onSelect();
        if (!expanded) onOpen();
      }}
      onKeyDown={(e) => {
        rowProps.onKeyDown?.(e as never);
        rest.onKeyDown?.(e);
      }}
      className={cn(
        "group/row relative -mx-4 flex h-9 cursor-default items-center gap-2.5 border-b border-border/45 pl-6 pr-3 outline-none transition-colors duration-75",
        expanded ? "bg-selected" : selected ? "bg-hover" : "hover:bg-hover",
        "focus-visible:bg-hover focus-visible:shadow-[inset_0_0_0_1.5px_var(--ring)]",
      )}
    >
      {(selected || expanded) && <span aria-hidden className="absolute inset-y-[7px] left-[9px] w-[2px] rounded-full bg-foreground/80" />}
      {/* Paper's Compact view: priority and deadline open their pickers in place. */}
      {chipEditors ? (
        chipEditors.priority(
          <button
            type="button"
            tabIndex={-1}
            aria-label={`Priority: ${PRIORITY_LABEL[t.priority]}`}
            onClick={(e) => e.stopPropagation()}
            className="-m-1 inline-flex shrink-0 rounded p-1 outline-none hover:bg-foreground/[0.06] data-[state=open]:bg-foreground/[0.06]"
          >
            <PriorityIcon priority={t.priority} className={cn("size-4 shrink-0", t.priority === "none" && "opacity-60")} />
          </button>,
        )
      ) : (
        <PriorityIcon priority={t.priority} className={cn("size-4 shrink-0", t.priority === "none" && "opacity-60")} />
      )}
      <span
        className="w-[var(--console-id-w,6.5ch)] shrink-0 truncate text-[12.5px] leading-none text-subtle tabular"
        title={t.readableId === "DSK-0" ? "Not synced yet" : t.readableId}
      >
        {t.readableId === "DSK-0" ? "—" : t.readableId}
      </span>
      <StatusToggle status={t.status} onToggle={onToggleComplete} title={t.title} className="-ml-0.5" />
      <span className="flex min-w-0 flex-1 items-center gap-2">
        <span
          title={t.title}
          className={cn("min-w-0 truncate text-[13.5px] font-[450] leading-none", closed ? "text-muted-foreground" : "text-foreground")}
        >
          {t.title}
        </span>
        {plannedToday && !closed && <TodayStar className="size-3 shrink-0" />}
        {!closed && t.checklist.length > 0 && (
          <span className="inline-flex shrink-0 items-center gap-1 text-[12px] leading-none text-subtle tabular" aria-label={`Checklist ${doneItems} of ${t.checklist.length}`}>
            <ProjectGlyph progress={{ done: doneItems, total: t.checklist.length }} className="size-3 text-subtle" />
            {doneItems}/{t.checklist.length}
          </span>
        )}
        {!closed && t.description && <StickyNote className="size-3 shrink-0 text-subtle" aria-label="Has notes" />}
        {carriedFrom && (
          <span className="shrink-0 text-[12px] leading-none">
            <CarriedLabel from={carriedFrom} today={today} />
          </span>
        )}
      </span>
      <span className="flex shrink-0 items-center gap-1.5 text-[12px] leading-none">
        <SyncBadge mark={sync} />
        <SnoozeBadge task={t} timezone={timezone} />
        <RepeatBadge task={t} />
        {t.labels.length > 0 && !closed && (
          <span className="hidden items-center gap-1.5 @min-[880px]/main:inline-flex">
            {t.labels.slice(0, 2).map((l) => (
              <LabelPill key={l} label={l} />
            ))}
            {extraLabels > 0 && <span className={metaChip} title={t.labels.slice(2).join(", ")}>+{extraLabels}</span>}
          </span>
        )}
        {t.deadline &&
          !closed &&
          (chipEditors ? (
            chipEditors.deadline(
              <button
                type="button"
                tabIndex={-1}
                aria-label="Change deadline"
                onClick={(e) => e.stopPropagation()}
                className={cn(metaChip, "outline-none hover:bg-foreground/[0.06] data-[state=open]:bg-foreground/[0.06]")}
              >
                <DeadlineLabel deadline={t.deadline} today={today} className="gap-1 [&_svg]:size-3" />
              </button>,
            )
          ) : (
            <span className={metaChip}>
              <DeadlineLabel deadline={t.deadline} today={today} className="gap-1 [&_svg]:size-3" />
            </span>
          ))}
        {place && (
          <span className={cn(metaChip, "hidden max-w-[200px] @min-[640px]/main:inline-flex")} title={place}>
            <span className="truncate">{place}</span>
          </span>
        )}
        {trailing}
        <span
          className={cn("flex w-6 justify-center transition-opacity", selected || expanded ? "opacity-100" : "opacity-0 group-hover/row:opacity-100 focus-within:opacity-100")}
          onClick={(e) => e.stopPropagation()}
        >
          {menu}
        </span>
      </span>
      <RowHoverActions selected={selected || expanded} className="right-[38px] top-[6px] h-6">
        {quickActions}
      </RowHoverActions>
    </div>
  );
}
